
import { useState, useCallback, useRef, useEffect } from 'react';

const DEFAULT_MAX_SECONDS = 60;

const VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  facingMode: 'user',
  height: { ideal: 720, max: 720 },
  frameRate: { ideal: 30, max: 30 },
};

function getMediaNotAvailableMessage(): string | null {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
    return 'Camera is not available. Use HTTPS or open this page from the same device (localhost).';
  }
  return null;
}

export interface VideoRecorderModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the recorded blob and duration in seconds when user clicks Continue */
  onComplete: (blob: Blob, durationSeconds: number) => void;
  displayName: string;
  prayerBody: string;
  maxSeconds?: number;
}

type RecorderState = 'preview' | 'recording' | 'playback';

export function VideoRecorderModal({
  isOpen,
  onClose,
  onComplete,
  displayName,
  prayerBody,
  maxSeconds = DEFAULT_MAX_SECONDS,
}: VideoRecorderModalProps) {
  const [state, setState] = useState<RecorderState>('preview');
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playbackVideoRef = useRef<HTMLVideoElement | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);

  const stopAllTracks = useCallback(() => {
    audioStreamRef.current?.getTracks().forEach((t) => t.stop());
    audioStreamRef.current = null;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      setStream(null);
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  }, [stream]);

  const stopRecording = useCallback(() => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setState('playback');
  }, []);

  const startRecording = useCallback(async () => {
    const notAvail = getMediaNotAvailableMessage();
    if (notAvail) {
      setCameraError(notAvail);
      return;
    }
    if (!stream) {
      setCameraError('Camera not ready.');
      return;
    }
    try {
      setCameraError(null);
      const audioStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });
      audioStreamRef.current = audioStream;

      const videoTrack = stream.getVideoTracks()[0];
      const audioTrack = audioStream.getAudioTracks()[0];
      if (!videoTrack) {
        audioStream.getTracks().forEach((t) => t.stop());
        audioStreamRef.current = null;
        setCameraError('No video track.');
        return;
      }
      const tracks = [videoTrack, ...(audioTrack ? [audioTrack] : [])];
      const streamToRecord = new MediaStream(tracks);

      const mimeType = MediaRecorder.isTypeSupported('video/mp4')
        ? 'video/mp4'
        : MediaRecorder.isTypeSupported('video/webm')
          ? 'video/webm'
          : '';
      const options: MediaRecorderOptions = {
        ...(mimeType ? { mimeType } : {}),
        videoBitsPerSecond: 1_500_000,
        audioBitsPerSecond: 64_000,
      };
      const recorder = new MediaRecorder(streamToRecord, options);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];
      const blobType = recorder.mimeType || 'video/webm';
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        if (chunksRef.current.length) {
          const blob = new Blob(chunksRef.current, { type: blobType });
          setRecordedBlob(blob);
          setPlaybackUrl(URL.createObjectURL(blob));
        }
        audioStreamRef.current?.getTracks().forEach((t) => t.stop());
        audioStreamRef.current = null;
      };
      recorder.start(1000);
      setState('recording');
      setRecordingSeconds(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds((s) => {
          if (s >= maxSeconds - 1) {
            if (recordingTimerRef.current) {
              clearInterval(recordingTimerRef.current);
              recordingTimerRef.current = null;
            }
            stopRecording();
            return maxSeconds;
          }
          return s + 1;
        });
      }, 1000);
    } catch {
      setCameraError('Could not access microphone.');
    }
  }, [maxSeconds, stream, stopRecording]);

  // Acquire camera when modal opens (no dimension or aspect-ratio constraints)
  useEffect(() => {
    if (!isOpen) return;
    let s: MediaStream | null = null;
    const acquire = async () => {
      const notAvail = getMediaNotAvailableMessage();
      if (notAvail) {
        setCameraError(notAvail);
        return;
      }
      try {
        setCameraError(null);
        s = await navigator.mediaDevices.getUserMedia({
          video: VIDEO_CONSTRAINTS,
          audio: false,
        });
        setStream(s);
      } catch {
        setCameraError('Could not access camera. Please allow camera access and try again.');
      }
    };
    acquire();
    return () => {
      if (s) s.getTracks().forEach((t) => t.stop());
      setStream(null);
    };
  }, [isOpen]);

  // Attach stream to video in preview/recording
  useEffect(() => {
    if (!stream || !videoRef.current || state === 'playback') return;
    const video = videoRef.current;
    video.srcObject = stream;
    video.muted = true;
    video.play().catch(() => {});
    return () => {
      video.srcObject = null;
    };
  }, [stream, state]);

  // Playback: set src and play
  useEffect(() => {
    if (state !== 'playback' || !playbackUrl || !playbackVideoRef.current) return;
    const el = playbackVideoRef.current;
    el.src = playbackUrl;
    el.play().catch(() => {});
  }, [state, playbackUrl]);

  const handleClose = useCallback(() => {
    const urlToRevoke = playbackUrl;
    stopAllTracks();
    setState('preview');
    setRecordedBlob(null);
    setPlaybackUrl(null);
    setRecordingSeconds(0);
    setCameraError(null);
    onClose();
    if (urlToRevoke) {
      setTimeout(() => URL.revokeObjectURL(urlToRevoke), 100);
    }
  }, [playbackUrl, stopAllTracks, onClose]);

  const handleReRecord = useCallback(() => {
    const urlToRevoke = playbackUrl;
    setPlaybackUrl(null);
    setRecordedBlob(null);
    setRecordingSeconds(0);
    setState('preview');
    if (urlToRevoke) {
      setTimeout(() => URL.revokeObjectURL(urlToRevoke), 100);
    }
  }, [playbackUrl]);

  const handleContinue = useCallback(() => {
    if (recordedBlob) {
      onComplete(recordedBlob, recordingSeconds);
      handleClose();
    }
  }, [recordedBlob, recordingSeconds, onComplete, handleClose]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      const urlToRevoke = playbackUrl;
      setState('preview');
      setRecordedBlob(null);
      setPlaybackUrl(null);
      setRecordingSeconds(0);
      if (urlToRevoke) {
        setTimeout(() => URL.revokeObjectURL(urlToRevoke), 100);
      }
    }
  }, [isOpen, playbackUrl]);

  if (!isOpen) return null;

  const isPreview = state === 'preview';
  const isRecording = state === 'recording';
  const isPlayback = state === 'playback';

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 flex flex-col items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Record video response"
    >
      {isPreview && (
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-4 left-4 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 focus:outline-none focus:ring-2 focus:ring-white"
          aria-label="Close"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}

      <div className="relative w-full max-h-[90vh] flex items-center justify-center flex-1 px-4">
        <div
          className="relative w-full sm:max-w-xl bg-black overflow-hidden rounded-lg"
          style={{
            aspectRatio: '9 / 16',
            maxHeight: '90vh',
          }}
        >
          {cameraError && isPreview && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 bg-black text-white text-center">
              <p className="text-sm text-amber-300">{cameraError}</p>
              <button
                type="button"
                onClick={() => {
                  setCameraError(null);
                  navigator.mediaDevices
                    ?.getUserMedia({ video: VIDEO_CONSTRAINTS, audio: false })
                    .then((s) => {
                      setStream(s);
                    })
                    .catch(() => setCameraError('Could not access camera.'));
                }}
                className="mt-4 px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 font-medium"
              >
                Retry camera
              </button>
            </div>
          )}

          {!cameraError && (isPreview || isRecording) && (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="absolute inset-0 w-full h-full object-cover"
                style={{ transform: 'scaleX(-1)' }}
              />
              <div className="absolute top-0 left-0 right-0 p-3 bg-black/70 text-white text-sm font-medium flex items-center justify-between gap-2">
                {isRecording ? (
                  <span className="flex items-center gap-2">
                    <span className="flex h-2.5 w-2.5 rounded-full bg-red-500 animate-pulse" aria-hidden />
                    <span className="rounded px-2 py-1 bg-red-600/90 text-white text-sm font-medium">
                      {recordingSeconds}s / {maxSeconds}s
                    </span>
                  </span>
                ) : (
                  <span />
                )}
                <span>{displayName}</span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-3 bg-black/70 text-white text-sm">
                <p className="line-clamp-3">{prayerBody}</p>
              </div>
              <div className="absolute bottom-24 left-0 right-0 flex justify-center pointer-events-none">
                <div className="pointer-events-auto">
                  {isPreview ? (
                    <button
                      type="button"
                      onClick={startRecording}
                      aria-label="Start recording"
                      className="w-16 h-16 sm:w-[4.8rem] sm:h-[4.8rem] rounded-full bg-red-600 hover:bg-red-700 active:bg-red-800 shadow-lg ring-4 ring-white/30 transition-colors flex items-center justify-center"
                    >
                      <span className="sr-only">Start Recording</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={stopRecording}
                      aria-label="Stop recording"
                      className="w-16 h-16 sm:w-[4.8rem] sm:h-[4.8rem] rounded-full bg-red-600 hover:bg-red-700 active:bg-red-800 shadow-lg ring-4 ring-white/30 transition-colors flex items-center justify-center"
                    >
                      <span className="w-6 h-6 rounded-sm bg-white" aria-hidden />
                    </button>
                  )}
                </div>
              </div>
            </>
          )}

          {isPlayback && playbackUrl && (
            <>
              <video
                ref={playbackVideoRef}
                src={playbackUrl}
                playsInline
                controls
                className="absolute inset-0 w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={handleReRecord}
                className="absolute top-4 left-4 z-10 px-4 py-2 rounded-lg bg-white/90 text-slate-900 font-medium hover:bg-white focus:outline-none focus:ring-2 focus:ring-white min-h-[44px]"
              >
                Record Again
              </button>
              <button
                type="button"
                onClick={handleContinue}
                className="absolute top-4 right-4 z-10 px-4 py-2 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-400 min-h-[44px]"
              >
                Continue
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
